/**
 * Merkt sich, dass die API unsere Anmeldung abgelehnt hat.
 *
 * WARUM das nötig ist: Wird eine Keycloak-Session serverseitig beendet — etwa
 * weil ein Admin die Sessions des Nutzers abgemeldet oder ein Attribut
 * geändert hat, oder weil in einem anderen Client des geteilten Realms ein
 * Logout stattfand —, bleibt der Access-Token im Cookie formal gültig: sein
 * `exp` liegt Tage in der Zukunft. Lokal ist der Session also nichts
 * anzusehen, und es wird auch kein Refresh ausgelöst. Nur die API weiß, dass
 * der Token nicht mehr angenommen wird.
 *
 * Deshalb dieses Signal: der Apollo-ErrorLink schreibt hinein, der Hinweis
 * im UI liest daraus (über `useSessionNeedsRenewal`).
 *
 * Ein externer Store statt React-Context, weil der ErrorLink außerhalb des
 * Komponentenbaums lebt. Frei von React- und Apollo-Importen.
 */

/** Warum die API abgelehnt hat — bestimmt, ob eine Neuanmeldung hilft. */
export type AuthRejectionReason = 'unauthorized' | 'missing-claim';

let rejection: AuthRejectionReason | null = null;
const listeners = new Set<() => void>();

/**
 * Hält den Grund fest und benachrichtigt die Zuhörer — aber nur bei einer
 * echten Änderung. Bei einer toten Session schlagen mehrere Abfragen
 * gleichzeitig fehl; ohne diese Sperre würde jede davon ein Rendern auslösen.
 */
export function markSessionRejected(reason: AuthRejectionReason): void {
  if (rejection === reason) return;
  rejection = reason;
  listeners.forEach((listener) => listener());
}

/** Für `useSyncExternalStore` — liefert einen stabilen Wert, kein Objekt. */
export function getSessionRejection(): AuthRejectionReason | null {
  return rejection;
}

export function subscribeSessionRejection(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Setzt das Signal zurück. Im Betrieb kaum nötig, weil die Erneuerung einen
 * vollen Seitenwechsel auslöst und den Modulzustand damit verwirft — aber
 * nötig, damit Tests unabhängig voneinander laufen.
 */
export function resetSessionRejection(): void {
  rejection = null;
}
